/**
 * The goods taxonomy and restricted-goods rulesets, edited from the catalogue console
 * (ADR-0062, M11-4).
 *
 * ## Corridors: three may be published, one is read
 *
 * §7 asks for "a per-corridor ruleset", and the maintainer ruled that LB-LB, LB-SY and SY-SY may
 * all be published. **Orders read exactly one**: `ORDER_CORRIDOR`, which `orders.service.ts`
 * imports rather than restating. A ruleset for any other corridor is on file for when an Order
 * learns its corridor from the two countries it moves between, and is read by nothing until then —
 * so the screen marks it, and the marker is driven by this constant rather than a copy of it.
 *
 * ## `restricted` is recorded, not enforced
 *
 * The creation and acceptance guards test `prohibited` alone; `restricted_categories` is read by
 * nothing (the seed's `perishable_food` is handled exactly like `general`). By the maintainer's
 * ruling the list stays editable and the screen says so. What "restricted" should do is Q11's.
 *
 * ## A category code is never renamed
 *
 * `orders.goods_category` is a foreign key, so a code an Order used must keep resolving. A category
 * is added under a code, its three names amended, and retired by status — never deleted, never
 * recoded. The code shape is the `snake_case` the seed and the loader already use.
 */
import { z } from 'zod';
import { REFERENCE_STATUSES } from './geography.js';
import { renderedText } from './rendered-text.js';
export const CORRIDORS = ['LB-LB', 'LB-SY', 'SY-SY'];
/** The one corridor Orders read today. See the header. */
export const ORDER_CORRIDOR = 'LB-LB';
const name = z.string().trim().min(1).max(200);
const note = z.string().trim().min(1).max(500);
const reviewDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'review_date_format');
const categoryCode = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/u, 'category_code_format');
const rulesetVersion = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/u, 'ruleset_version_format');
const instant = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/u, 'iso_instant');
// ADR-0072 exemption reasons shared by the write-body declarations below.
const CATEGORY_CODE_EXEMPT = 'a category code: snake_case with a letter first (category_code_format), so no digit run starts a word';
const REVIEW_DATE_EXEMPT = 'a YYYY-MM-DD date (review_date_format), which holds no phone-shaped digit run';
const noteExempt = (table) => `stored only in the config.${table}.amended event payload; no view renders it`;
export const addCategorySchema = z
    .object({
    code: categoryCode,
    nameAr: name,
    nameEn: name,
    nameFr: name.nullable(),
    note,
})
    .strict();
export const addCategoryRendered = renderedText(addCategorySchema, {
    prose: ['nameAr', 'nameEn', 'nameFr'],
    exempt: { code: CATEGORY_CODE_EXEMPT, note: noteExempt('goods_categories') },
});
export const amendCategorySchema = z
    .object({
    nameAr: name,
    nameEn: name,
    nameFr: name.nullable(),
    status: z.enum(REFERENCE_STATUSES),
    note,
})
    .strict();
export const amendCategoryRendered = renderedText(amendCategorySchema, {
    prose: ['nameAr', 'nameEn', 'nameFr'],
    exempt: { note: noteExempt('goods_categories') },
});
/**
 * A new ruleset version for one corridor.
 *
 * Both lists are required and either may be empty: a corridor that prohibits nothing is a ruling,
 * and an absent list would read as one nobody made. Codes are checked against the taxonomy by the
 * shared writer, which refuses an unknown or retired category and a code on both lists; the route
 * refuses a version that does not take effect after the latest on file for its corridor.
 */
export const publishRulesetSchema = z
    .object({
    corridor: z.enum(CORRIDORS),
    version: rulesetVersion,
    prohibited: z.array(categoryCode).max(200),
    restricted: z.array(categoryCode).max(200),
    effectiveFrom: instant,
    owner: z.string().trim().min(1).max(200),
    reviewDate,
    note,
})
    .strict();
export const publishRulesetRendered = renderedText(publishRulesetSchema, {
    prose: ['owner'],
    // A version is a reference, and ruleset_version_format admits a digit run such as 0312345678.
    refs: ['version'],
    exempt: {
        prohibited: CATEGORY_CODE_EXEMPT,
        restricted: CATEGORY_CODE_EXEMPT,
        effectiveFrom: 'an ISO instant ending in Z (iso_instant), which holds no phone-shaped digit run',
        reviewDate: REVIEW_DATE_EXEMPT,
        note: noteExempt('restricted_goods_rulesets'),
    },
});
//# sourceMappingURL=catalogue.js.map